import {
  ArrowRight,
  BadgeCheck,
  CircleAlert,
  CircleCheck,
  CloudUpload,
  FileSpreadsheet,
  FileUp,
  LoaderCircle,
} from "lucide-react";
import { Link } from "react-router";
import { useNavigate } from "react-router";
import { useRef, useState } from "react";
import { setPendingUpload } from "@/lib/import/pending-upload";

const ACCEPTED_MIME_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
  "text/plain",
  "application/csv",
]);

const ACCEPTED_EXTENSIONS = [".csv", ".xlsx", ".xls"];

const WORKFLOW_STEPS: {
  title: string;
  description: string;
  icon: typeof FileUp;
}[] = [
  {
    title: "Pick sheets & headers",
    description: "Tick the sheets to import and check where their headers and data start.",
    icon: FileUp,
  },
  {
    title: "Choose a template",
    description: "Pick the saved template for each sheet, or none to map it yourself.",
    icon: BadgeCheck,
  },
  {
    title: "Export CSV",
    description: "Generate Shopify-ready rows after review.",
    icon: ArrowRight,
  },
];

export function UploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const openFilePicker = () => {
    if (loading) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);

    if (!isAcceptedFile(file)) {
      setError(
        "Upload a spreadsheet with a .csv, .xlsx, or .xls extension.",
      );
      return;
    }

    setError(null);
    setPendingUpload({ file });
    navigate("/workbooks/import");
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (loading) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelected(files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      handleFileSelected(files[0]);
    }
    e.currentTarget.value = "";
  };

  const dropzoneClassName = [
    "group relative flex min-h-[320px] cursor-pointer flex-col justify-center rounded-[10px] border border-dashed px-6 py-10 text-center transition sm:px-10",
    loading ? "cursor-wait" : "hover:-translate-y-0.5",
    isDragging
      ? "border-zinc-950 bg-zinc-100 shadow-sm dark:border-zinc-100 dark:bg-surface-2"
      : "border-zinc-300 bg-zinc-50/80 hover:border-zinc-400 hover:bg-white dark:border-border dark:bg-background/70 dark:hover:border-zinc-500 dark:hover:bg-surface",
  ].join(" ");

  return (
    <>
      <div className="mx-auto w-full max-w-6xl px-4 py-6 lg:px-6 lg:py-8">
        <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
              Workbook intake
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-100">
              Upload supplier workbook
            </h1>
            <p className="mt-2 text-[13px] leading-6 text-zinc-600 dark:text-zinc-400">
              Start a new listing run by uploading the supplier file (CSV or
              Excel). Proly will let you configure the header layout before
              importing.
            </p>
          </div>
          <Link
            to="/workbooks"
            className="inline-flex h-9 w-fit items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 text-[13px] font-medium text-zinc-700 transition hover:bg-zinc-50 hover:text-zinc-950 dark:border-border dark:bg-surface dark:text-zinc-300 dark:hover:bg-surface-2 dark:hover:text-zinc-100"
          >
            View workbooks
            <ArrowRight size={15} />
          </Link>
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_330px]">
          <section className="overflow-hidden rounded-[10px] border border-zinc-200 bg-white shadow-[0_12px_30px_-24px_rgb(0_0_0/0.35)] dark:border-border dark:bg-surface">
            <div className="flex items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4 dark:border-border">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-700 dark:bg-surface-2 dark:text-zinc-200">
                  <FileSpreadsheet size={18} />
                </span>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">
                    Supplier workbook
                  </h2>
                  <p className="mt-1 text-[12px] leading-5 text-zinc-500 dark:text-zinc-400">
                    Accepted formats: .csv, .xlsx, and .xls. One workbook per upload.
                  </p>
                </div>
              </div>
              <span className="hidden rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] font-medium text-zinc-600 dark:bg-surface-2 dark:text-zinc-300 sm:inline-flex">
                Step 1 of 4
              </span>
            </div>

            <div className="p-4 sm:p-5">
              <input
                ref={fileInputRef}
                id="workbook-file"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleChange}
                disabled={loading}
                className="sr-only"
              />

              <div
                role="button"
                tabIndex={loading ? -1 : 0}
                aria-label="Choose a CSV or Excel workbook to upload"
                aria-disabled={loading}
                onClick={openFilePicker}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openFilePicker();
                  }
                }}
                onDrop={handleDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!loading) setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                className={dropzoneClassName}
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[10px] border border-zinc-200 bg-white text-zinc-800 shadow-sm transition group-hover:scale-[1.03] dark:border-border dark:bg-surface dark:text-zinc-100">
                  {loading ? (
                    <LoaderCircle size={28} className="animate-spin" />
                  ) : (
                    <CloudUpload size={30} />
                  )}
                </div>

                <div className="mx-auto mt-6 max-w-md">
                  <p className="text-lg font-semibold tracking-tight text-zinc-950 dark:text-zinc-100">
                    {loading ? "Uploading workbook" : "Drop file here"}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
                    {loading
                      ? "Hold on while the workbook is saved and parsed."
                      : "Drag a CSV or Excel file into this area, or click anywhere in the panel to browse."}
                  </p>
                </div>

                <span className="mx-auto mt-6 inline-flex h-10 items-center gap-2 rounded-md bg-zinc-950 px-4 text-sm font-medium text-white transition group-hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-950 dark:group-hover:bg-zinc-200">
                  <FileUp size={16} />
                  {loading ? "Processing file" : "Select file"}
                </span>
              </div>

              <div className="mt-4 rounded-[10px] border border-zinc-200 bg-zinc-50/80 px-4 py-3 dark:border-border dark:bg-background/60">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white text-zinc-600 dark:bg-surface dark:text-zinc-300">
                      <FileSpreadsheet size={17} />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {selectedFile?.name ?? "No file selected"}
                      </p>
                      <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                        {selectedFile
                          ? `${formatBytes(selectedFile.size)} selected`
                          : "Choose a CSV or Excel workbook to begin."}
                      </p>
                    </div>
                  </div>
                  <div
                    aria-live="polite"
                    className={[
                      "inline-flex w-fit items-center gap-2 self-end rounded-full px-2.5 py-1 text-xs font-medium sm:self-auto",
                      error
                        ? "bg-red-50 text-red-700 dark:bg-red-950/35 dark:text-red-300"
                        : "bg-white text-zinc-600 dark:bg-surface dark:text-zinc-300",
                    ].join(" ")}
                  >
                    {loading ? (
                      <>
                        <LoaderCircle size={14} className="animate-spin" />
                        Uploading
                      </>
                    ) : error ? (
                      <>
                        <CircleAlert size={14} />
                        Invalid
                      </>
                    ) : selectedFile && !error ? (
                      <>
                        <CircleCheck size={14} className="text-emerald-600 dark:text-emerald-300" />
                        Ready
                      </>
                    ) : (
                      "Waiting"
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="mt-4 flex gap-3 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/70 dark:bg-red-950/35 dark:text-red-300">
                  <CircleAlert size={18} className="mt-0.5 shrink-0" />
                  <p className="font-medium">{error}</p>
                </div>
              )}
            </div>
          </section>

          <aside className="space-y-5">
            <section className="rounded-[10px] border border-zinc-200 bg-white p-5 dark:border-border dark:bg-surface">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-100 text-zinc-700 dark:bg-surface-2 dark:text-zinc-200">
                  <CircleCheck size={17} />
                </span>
                <h2 className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">
                  What happens next
                </h2>
              </div>
              <ol className="mt-5 space-y-4">
                {WORKFLOW_STEPS.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <li key={step.title} className="flex gap-3">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-border dark:bg-background dark:text-zinc-300">
                        <Icon size={15} />
                      </span>
                      <div>
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {index + 1}. {step.title}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-zinc-500 dark:text-zinc-400">
                          {step.description}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>

            <section className="rounded-[10px] border border-zinc-200 bg-white p-5 dark:border-border dark:bg-surface">
              <h2 className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">
                Upload checklist
              </h2>
              <ul className="mt-4 space-y-3 text-xs leading-5 text-zinc-600 dark:text-zinc-400">
                <li className="flex gap-2">
                  <CircleCheck size={15} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-300" />
                  CSV, XLSX, and XLS formats are all supported.
                </li>
                <li className="flex gap-2">
                  <CircleCheck size={15} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-300" />
                  Use the latest supplier file when resuming a listing run.
                </li>
                <li className="flex gap-2">
                  <CircleCheck size={15} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-300" />
                  You choose the sheets and templates; nothing is auto-assigned.
                </li>
              </ul>
            </section>
          </aside>
        </div>
      </div>

    </>
  );
}

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  const hasAcceptedExtension = ACCEPTED_EXTENSIONS.some((extension) =>
    name.endsWith(extension),
  );

  return hasAcceptedExtension || ACCEPTED_MIME_TYPES.has(file.type);
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB"] as const;
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** index;

  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}
